import { Check, CheckCircle2, Clock, Star, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { BusinessProfile, ScoreBreakdown } from '../../types'
import { BRAND } from '../../config'
import {
  HeaderMetaChip,
  ReportSectionCard,
  ReportSectionHeader,
} from './ReportSectionCard'

interface LocalListingsProfileCardProps {
  profile: BusinessProfile
  reviewCount?: number
  averageRating?: number
  /** Local Listings breakdowns — used to show each row's earned/max points. */
  breakdowns?: ScoreBreakdown[]
  /** Reviews added in the last 30 days — shown as user-submitted context. */
  recentReviewCount?: number
}

type ProfileItemState = 'present' | 'missing' | 'pending'

interface ProfileItem {
  key: string
  title: string
  detail?: string
  state: ProfileItemState
  /** Score signals this row maps to, used to look up its points. */
  signals?: string[]
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Strips protocol + www so a website URL reads like a clean domain. */
function prettyDomain(url: string): string {
  return url
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '')
}

/** Renders Google's 0–4 price level as $ symbols; null when not provided. */
function priceSymbols(level: number): string | null {
  if (level == null || level < 0) return null
  if (level === 0) return null
  return '$'.repeat(Math.min(level, 4))
}

/** Heuristic: does the description reference the business category/keywords? */
function descriptionHasKeywords(profile: BusinessProfile): boolean {
  const desc = (profile.description ?? '').toLowerCase()
  if (!desc) return false
  const terms = [profile.category, ...(profile.categories ?? [])]
    .filter(Boolean)
    .map(c => c.toLowerCase())
  return terms.some(term => term && desc.includes(term))
}

function VerifiedTitleBadge({ label }: { label: string }) {
  return (
    <span
      className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#1877f2]"
      title={label}
      aria-label={label}
    >
      <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
    </span>
  )
}

// ─── Profile content item row ─────────────────────────────────────────────────

function ItemRow({ item, points }: { item: ProfileItem; points?: ScoreBreakdown }) {
  const { t } = useTranslation('report')
  // Prefer the scored breakdown for the status icon so it matches the points;
  // a partially-earned signal (e.g. some points but not full) reads as a warning.
  const state: ProfileItemState = points
    ? points.is_positive
      ? 'present'
      : points.points > 0
        ? 'pending'
        : 'missing'
    : item.state
  const icon =
    state === 'present' ? (
      <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" strokeWidth={2} />
    ) : state === 'pending' ? (
      <Clock className="w-4 h-4 text-amber-500 shrink-0" />
    ) : (
      <XCircle className="w-4 h-4 text-red-500 shrink-0" strokeWidth={2} />
    )

  return (
    <div className="flex items-start gap-2.5 py-2">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-800">{item.title}</p>
        {item.detail && (
          <p className="mt-0.5 text-xs leading-relaxed text-gray-500">{item.detail}</p>
        )}
      </div>
      {points && (
        <span className="mt-0.5 shrink-0 rounded-full bg-gray-100 px-2.5 py-0.5 text-[10px] font-medium tabular-nums text-gray-500">
          {t('category.pts', { points: points.points, max: points.max_points })}
        </span>
      )}
    </div>
  )
}

// ─── LocalListingsProfileCard ──────────────────────────────────────────────────

/**
 * Renders a detailed Google Business Profile content breakdown for the
 * Local Listings category, surfacing the real profile values fetched from the
 * provider (website, description, hours, phone, price level, service options,
 * social links, keyword relevance) plus user-submitted content.
 */
export function LocalListingsProfileCard({
  profile,
  reviewCount,
  averageRating,
  breakdowns = [],
  recentReviewCount,
}: LocalListingsProfileCardProps) {
  const { t } = useTranslation('report')

  const hoursCount = Object.keys(profile.hours ?? {}).length
  const price = priceSymbols(profile.price_level)
  const social = profile.social_links ?? []
  const totalReviews = reviewCount ?? profile.review_count ?? 0
  const rating = averageRating ?? profile.rating ?? 0

  // Look up the scored breakdown for a row from its candidate signals.
  const findPoints = (signals?: string[]) =>
    signals ? breakdowns.find(b => signals.includes(b.signal)) : undefined

  const profileItems: ProfileItem[] = [
    {
      key: 'website',
      title: t('localListings.items.website'),
      detail: profile.website
        ? prettyDomain(profile.website)
        : t('localListings.missing.website'),
      state: profile.website ? 'present' : 'missing',
      signals: ['has_website_listing', 'missing_website_listing'],
    },
    {
      key: 'description',
      title: t('localListings.items.description'),
      detail: profile.description
        ? `"${profile.description}"`
        : t('localListings.missing.description'),
      state: profile.description ? 'present' : 'missing',
      signals: ['detailed_description', 'short_description', 'no_description'],
    },
    {
      key: 'hours',
      title: t('localListings.items.hours'),
      detail:
        hoursCount > 0
          ? t('localListings.detail.hours')
          : t('localListings.missing.hours'),
      state: hoursCount > 0 ? 'present' : 'missing',
      signals: ['has_hours', 'incomplete_hours', 'missing_hours'],
    },
    {
      key: 'phone',
      title: t('localListings.items.phone'),
      detail: profile.phone || t('localListings.missing.phone'),
      state: profile.phone ? 'present' : 'missing',
      signals: ['has_phone', 'missing_phone'],
    },
    {
      key: 'verification',
      title: t('localListings.items.verification'),
      detail: profile.is_verified
        ? t('breakdown.is_verified')
        : t('breakdown.not_verified'),
      state: profile.is_verified ? 'present' : 'missing',
      signals: ['is_verified', 'not_verified'],
    },
    {
      key: 'price',
      title: t('localListings.items.priceRange'),
      detail: price
        ? `${price} · ${t('localListings.detail.priceRange')}`
        : t('localListings.missing.priceRange'),
      state: price ? 'present' : 'missing',
      signals: ['has_price_range', 'missing_price_range'],
    },
    {
      key: 'social',
      title: t('localListings.items.socialLinks'),
      detail:
        social.length > 0
          ? social.map(prettyDomain).join(' · ')
          : t('localListings.missing.socialLinks'),
      state: social.length > 0 ? 'present' : 'missing',
      signals: ['has_social_links', 'missing_social_links'],
    },
    {
      key: 'keywords',
      title: t('localListings.items.keywordsInDescription'),
      detail: descriptionHasKeywords(profile)
        ? t('localListings.detail.keywordsInDescription')
        : t('localListings.missing.keywordsInDescription'),
      state: descriptionHasKeywords(profile) ? 'present' : 'missing',
      signals: ['has_keywords_in_description', 'missing_keywords_in_description'],
    },
  ]

  const profileIcon = profile.profile_photo_url ? (
    <img
      src={profile.profile_photo_url}
      alt={profile.name}
      className="h-full w-full rounded-full object-cover"
      onError={e => {
        ;(e.target as HTMLImageElement).style.display = 'none'
      }}
    />
  ) : (
    <span className="text-sm font-bold text-white">
      {profile.name?.charAt(0)?.toUpperCase() ?? 'B'}
    </span>
  )

  return (
    <ReportSectionCard variant="nested" delay={0.2}>
      <ReportSectionHeader
        bordered={false}
        iconNode={
          <div
            className="flex h-full w-full items-center justify-center overflow-hidden rounded-full"
            style={profile.profile_photo_url ? undefined : { backgroundColor: BRAND.navy }}
          >
            {profileIcon}
          </div>
        }
        accentColor="#2563eb"
        title={t('localListings.profileTitle')}
        titleExtra={
          profile.is_verified ? (
            <VerifiedTitleBadge label={t('breakdown.is_verified')} />
          ) : undefined
        }
        subtitle={profile.name}
        right={
          <HeaderMetaChip>
            <span className="font-semibold text-[#1e2939]">{rating.toFixed(1)}</span>
            <Star className="mx-1 inline h-3 w-3 fill-[#fcc800] text-[#fcc800]" />
            <span>· {totalReviews.toLocaleString()} {t('localListings.reviewsLabel')}</span>
          </HeaderMetaChip>
        }
      />

      <div className="px-4 pb-5 sm:px-6">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
        {t('localListings.profileContent')}
      </p>
      <div className="divide-y divide-gray-50">
        {profileItems.map(item => (
          <ItemRow key={item.key} item={item} points={findPoints(item.signals)} />
        ))}
      </div>

      {/* User-submitted content */}
      <p className="mb-1 mt-5 text-xs font-semibold uppercase tracking-wider text-gray-400">
        {t('localListings.userContent')}
      </p>
      <div className="divide-y divide-gray-50">
        <ItemRow
          item={{
            key: 'reviews',
            title: t('localListings.items.qualityReviews'),
            detail: t('localListings.detail.qualityReviews', { count: totalReviews }),
            state: totalReviews > 0 ? 'present' : 'missing',
          }}
        />
        <ItemRow
          item={{
            key: 'rating',
            title: t('breakdown.review_rating', { rating: rating.toFixed(1) }),
            state: rating > 0 ? 'present' : 'missing',
          }}
        />
        <ItemRow
          item={{
            key: 'count',
            title: t('breakdown.review_count', { count: totalReviews }),
            state: totalReviews > 0 ? 'present' : 'missing',
          }}
        />
        {recentReviewCount != null && (
          <ItemRow
            item={{
              key: 'recency',
              title: t('breakdown.review_recency', { count: recentReviewCount }),
              state: recentReviewCount > 0 ? 'present' : 'missing',
            }}
          />
        )}
      </div>
      </div>
    </ReportSectionCard>
  )
}
